import type { BroodAttack } from '../../../data/full/poussin';
import { angleOf } from '../../../shared/angle';
import { wrap } from '../../../shared/scalar';
import type { AttackModule } from '../../attacks/types';
import { pullTrigger } from '../../attacks/trigger';
import { distanceSquared } from '../../../shared/vec';
import { hurtEnemy, keepWhere } from '../../effects';
import type { EnemyState, PlayerState, PropState, SimState, Vec2 } from '../../state';
import { statValue } from '../../stats';
import type { StepContext } from '../../systems/types';
import { closeIn, ownerOf } from '../../props';
import { flightTicks, flyHome, preyOf } from './swarm';

export const CHICK = 'chick';
export const CHARGING_CHICK = 'chargingChick';

const STEP = 4;
// Further than this at once, he was moved to another place (a new stage).
const JUMP = 200;

export const brood: AttackModule<BroodAttack> = {
  reads: ['attackCooldownMul', 'damageMul', 'pierceAdd', 'projectileCountAdd'],

  ammunition: true,

  step(ctx, player, attack, input) {
    const { state } = ctx;
    const count = bandSize(player, attack);
    player.steps ??= lineBehind(player, fileLength(count, attack));
    const chicks = keepBand(state, player, attack, count);
    const prey = input.fire ? preyOf(state.enemies, player, attack.reach) : [];
    const resting = chicks.filter((chick) => chick.kind === CHICK && chick.phase === 'held');
    if (!pullTrigger(player, prey.length > 0 && resting.length > 0, attack.cooldownTicks)) {
      return;
    }
    const pull = Math.floor(
      state.tick / statValue(player, 'attackCooldownMul', attack.cooldownTicks),
    );
    const sent = Math.floor((pull * count) / attack.turnBeats);
    const leaving = Math.floor(((pull + 1) * count) / attack.turnBeats) - sent;
    const first = sent % count;
    resting.sort((a, b) => wrap((a.rank ?? 0) - first, count) - wrap((b.rank ?? 0) - first, count));
    const damage = statValue(player, 'damageMul', attack.damage);
    const coming = pecksComing(chicks);
    resting.slice(0, leaving).forEach((chick, index) => {
      const target =
        prey.find((enemy) => (coming.get(enemy.id) ?? 0) < enemy.hp) ?? prey[index % prey.length];
      if (target === undefined) {
        return;
      }
      const pecks = statValue(player, 'pierceAdd', attack.pecks);
      coming.set(target.id, (coming.get(target.id) ?? 0) + damage * pecks);
      chick.phase = 'flying';
      chick.targetId = target.id;
      chick.ticksLeft = flightTicks(attack.reach + fileLength(count, attack), attack.speed);
      chick.damage = damage;
      chick.hitsLeft = pecks;
    });
    state.events.push({
      type: 'playerFired',
      playerId: player.id,
      x: player.x,
      y: player.y,
      angle: angleOf(player.aim),
    });
  },

  update(ctx) {
    const { state } = ctx;
    for (const player of state.players) {
      if (player.steps !== undefined) {
        const attack = attackOf(ctx, player);
        walk(player, player.steps, fileLength(bandSize(player, attack), attack));
      }
    }
    for (const prop of state.props ?? []) {
      if (prop.kind !== CHICK) {
        continue;
      }
      const owner = ownerOf(state, prop);
      const attack = attackOf(ctx, owner);
      const place = slotOf(owner, prop.rank ?? 0, attack.gap);
      if (prop.phase === 'held') {
        prop.x = place.x;
        prop.y = place.y;
      } else if (prop.phase === 'flying' || prop.phase === 'grounded') {
        peck(ctx, prop, attack);
      } else if (closeIn(prop, place, 0, attack.returnSpeed)) {
        prop.phase = 'held';
        prop.vx = prop.vy = 0;
      }
    }
  },

  reach(attack) {
    return attack.reach;
  },
};

export function bandSize(player: PlayerState, attack: BroodAttack): number {
  return statValue(player, 'projectileCountAdd', attack.chicks);
}

function bandOf(state: SimState, player: Pick<PlayerState, 'id'>): PropState[] {
  return (state.props ?? []).filter(
    (prop) => (prop.kind === CHICK || prop.kind === CHARGING_CHICK) && prop.playerId === player.id,
  );
}

export function keepBand(
  state: SimState,
  player: PlayerState,
  attack: BroodAttack,
  count: number,
): PropState[] {
  const band = bandOf(state, player);
  for (let rank = 0; band.length < count; rank += 1) {
    if (!band.some((chick) => chick.rank === rank)) {
      band.push(hatch(state, player, rank, slotOf(player, rank, attack.gap)));
    }
  }
  if (band.length <= count) {
    return band;
  }
  const out = band
    .filter((chick) => chick.kind === CHICK && chick.phase === 'held')
    .sort((a, b) => (b.rank ?? 0) - (a.rank ?? 0))
    .slice(0, band.length - count);
  if (out.length === 0) {
    return band;
  }
  const props = state.props ?? [];
  keepWhere(props, (prop) => !out.includes(prop));
  if (props.length === 0) {
    delete state.props;
  }
  return band.filter((chick) => !out.includes(chick));
}

function hatch(state: SimState, player: PlayerState, rank: number, at: Vec2): PropState {
  const chick: PropState = {
    id: state.nextEntityId,
    kind: CHICK,
    playerId: player.id,
    phase: 'held',
    x: at.x,
    y: at.y,
    prevX: at.x,
    prevY: at.y,
    vx: 0,
    vy: 0,
    damage: 0,
    ticksLeft: 0,
    hitsLeft: 0,
    rank,
  };
  state.nextEntityId += 1;
  (state.props ??= []).push(chick);
  return chick;
}

function peck(ctx: StepContext, chick: PropState, attack: BroodAttack): void {
  const { state } = ctx;
  let target = state.enemies.find((enemy) => enemy.id === chick.targetId && enemy.hp > 0);
  if (target === undefined) {
    target = nearestTo(state.enemies, chick, attack.range);
    if (target === undefined) {
      flyHome(chick);
      return;
    }
    chick.targetId = target.id;
    chick.phase = 'flying';
    chick.ticksLeft = flightTicks(attack.range, attack.speed);
  }
  if (chick.phase === 'flying') {
    chick.ticksLeft -= 1;
    if (!reachEdge(chick, target, attack.speed)) {
      if (chick.ticksLeft <= 0) {
        flyHome(chick);
      }
      return;
    }
    chick.phase = 'grounded';
    chick.vx = chick.vy = 0;
    chick.ticksLeft = 0;
  } else {
    stayAtEdge(chick, target);
  }
  if (chick.ticksLeft > 0) {
    chick.ticksLeft -= 1;
    return;
  }
  hurtEnemy(state, target, chick.damage, chick.playerId, chick);
  chick.hitsLeft -= 1;
  chick.ticksLeft = attack.peckTicks - 1;
  if (chick.hitsLeft <= 0) {
    flyHome(chick);
  }
}

function reachEdge(chick: PropState, target: EnemyState, speed: number): boolean {
  const dx = target.x - chick.x;
  const dy = target.y - chick.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  if (distance <= speed + target.radius) {
    stayAtEdge(chick, target);
    return true;
  }
  chick.vx = (dx / distance) * speed;
  chick.vy = (dy / distance) * speed;
  chick.x += chick.vx;
  chick.y += chick.vy;
  return false;
}

function stayAtEdge(chick: PropState, target: EnemyState): void {
  const dx = chick.x - target.x;
  const dy = chick.y - target.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  if (distance === 0) {
    return;
  }
  chick.x = target.x + (dx / distance) * target.radius;
  chick.y = target.y + (dy / distance) * target.radius;
}

function nearestTo(
  enemies: readonly EnemyState[],
  from: Vec2,
  range: number,
): EnemyState | undefined {
  let nearest: EnemyState | undefined;
  let best = range * range;
  for (const enemy of enemies) {
    const distance = distanceSquared(enemy, from);
    if (enemy.hp > 0 && distance <= best) {
      nearest = enemy;
      best = distance;
    }
  }
  return nearest;
}

function pecksComing(chicks: readonly PropState[]): Map<number, number> {
  const coming = new Map<number, number>();
  for (const chick of chicks) {
    if ((chick.phase === 'flying' || chick.phase === 'grounded') && chick.targetId !== undefined) {
      coming.set(
        chick.targetId,
        (coming.get(chick.targetId) ?? 0) + chick.damage * Math.max(0, chick.hitsLeft),
      );
    }
  }
  return coming;
}

function fileLength(count: number, attack: BroodAttack): number {
  return (count + 1) * attack.gap;
}

function lineBehind(player: PlayerState, length: number): number[] {
  return [player.x, player.y, player.x - player.aim.x * length, player.y - player.aim.y * length];
}

function walk(player: PlayerState, steps: number[], length: number): void {
  const awayX = player.x - (steps[0] ?? player.x);
  const awayY = player.y - (steps[1] ?? player.y);
  const away = awayX * awayX + awayY * awayY;
  if (away > JUMP * JUMP) {
    player.steps = lineBehind(player, length);
    return;
  }
  if (away >= STEP * STEP) {
    steps.unshift(player.x, player.y);
  }
  let fromX = player.x;
  let fromY = player.y;
  let walked = 0;
  for (let index = 0; index + 1 < steps.length; index += 2) {
    const x = steps[index] ?? fromX;
    const y = steps[index + 1] ?? fromY;
    walked += Math.sqrt((x - fromX) * (x - fromX) + (y - fromY) * (y - fromY));
    if (walked >= length) {
      steps.length = index + 2;
      return;
    }
    fromX = x;
    fromY = y;
  }
}

export function slotOf(player: PlayerState, rank: number, gap: number): Vec2 {
  const steps = player.steps ?? [];
  let left = (rank + 1) * gap;
  let fromX = player.x;
  let fromY = player.y;
  for (let index = 0; index + 1 < steps.length; index += 2) {
    const x = steps[index] ?? fromX;
    const y = steps[index + 1] ?? fromY;
    const span = Math.sqrt((x - fromX) * (x - fromX) + (y - fromY) * (y - fromY));
    if (span >= left && span > 0) {
      return { x: fromX + ((x - fromX) * left) / span, y: fromY + ((y - fromY) * left) / span };
    }
    left -= span;
    fromX = x;
    fromY = y;
  }
  return { x: fromX, y: fromY };
}

export function attackOf(ctx: Pick<StepContext, 'content'>, owner: PlayerState): BroodAttack {
  const attack = ctx.content.classes.get(owner.classId)?.attack;
  if (attack?.kind !== 'brood') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" has no band`);
  }
  return attack;
}
